import React, { useEffect, useCallback } from "react";
import { InlineNotification, Link as TextLink, useToast } from "@familyzone/component-library";
import { AttributeMapping, MergeConfig, ProviderData, SISProvider } from "./SisIntegration";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Text,
  Box,
  Button,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { colors } from "../../../theme";
import { MergeOptions, UserMergeKey } from "../SyncConfig/ConfigForm/MergeOptions";
import { extractSyncError } from "../../../utils/api/SyncApi";
import { saveSisSync } from "./mapSyncNodeToSISProvider";
import { useForm } from "react-hook-form";
import { TextFieldControl } from "../SyncConfig/ConfigForm/TextFieldControl";
import { CheckBoxControl } from "../SyncConfig/ConfigForm/CheckboxControl";
import {
  AttributeConfig,
  defaultYearAttributes,
  StoredOrDefaultYearAttributes,
  YearAttributes,
  YearAttributesToInput,
} from "./AttributeConfig";

interface Props {
  rowToEdit: SISProvider | null;
  show: boolean;
  handleHide: () => void;
  handleSubmit: () => void;
  multipleSyncsConfigured?: boolean;
}

export interface PowerSchoolInputs {
  respect_enrolment_date: boolean;
  respect_sessions: boolean;
  integration_type: string;
  name: string;
  endpoint_url: string;
  token_url: string;
  client_id: string;
  client_secret: string;
  user_merge_priority: number;
  user_merge_key: UserMergeKey;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;
  sync_year_attributes: boolean;
  year_attribute_mappings?: YearAttributes;
  compatibility_mode: boolean;

  // non-editable but included for payload construction consistency
  id?: string;
  enabled: boolean;
  excluded_schools_list: string[];
  type: "powerschool";
}

export const SisPowerSchoolForm: React.FC<Props> = ({
  rowToEdit,
  show,
  handleHide,
  handleSubmit: handleSubmitExternal,
  multipleSyncsConfigured = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<PowerSchoolInputs>({
    mode: "onBlur",
    defaultValues: {
      respect_enrolment_date: false,
      respect_sessions: false,
      integration_type: "Powerschool",
      name: "",
      endpoint_url: "",
      token_url: "",
      client_id: "",
      client_secret: "",
      user_merge_priority: 10,
      user_merge_key: UserMergeKey.Email,
      user_merge_expression: undefined,
      user_merge_attach_only: false,
      id: undefined,
      enabled: true,
      excluded_schools_list: [],
      type: "powerschool",
      sync_year_attributes: true,
      year_attribute_mappings: defaultYearAttributes,
      compatibility_mode: false,
    },
  });
  const syncYearAttributesWatch = watch("sync_year_attributes");
  const { successToast, errorToast } = useToast();

  useEffect(() => {
    reset({
      respect_enrolment_date: rowToEdit?.respect_enrolment_date || false,
      respect_sessions: rowToEdit?.respect_sessions || false,
      integration_type: "Powerschool",
      name: rowToEdit?.name || "",
      endpoint_url: rowToEdit?.api_configuration.endpoint_url || "",
      token_url: rowToEdit?.api_configuration.token_url || "",
      client_id: rowToEdit?.api_configuration.client_id || "",
      client_secret: rowToEdit?.api_configuration.client_secret || "",
      user_merge_priority: rowToEdit?.merge_config?.priority || 10,
      user_merge_expression: rowToEdit?.merge_config?.computed?.expression || undefined,
      user_merge_key: rowToEdit?.merge_config?.computed
        ? UserMergeKey.Computed
        : rowToEdit?.merge_config?.email?.stripped
        ? UserMergeKey.EmailStripped
        : UserMergeKey.Email,
      user_merge_attach_only: rowToEdit?.merge_config?.attachOnly || false,
      id: rowToEdit?.id,
      enabled: rowToEdit ? rowToEdit.enabled : true,
      excluded_schools_list: rowToEdit?.excluded_schools_list || [],
      type: "powerschool",
      sync_year_attributes: rowToEdit?.sync_attributes?.years?.enabled ?? true,
      year_attribute_mappings: StoredOrDefaultYearAttributes(rowToEdit?.sync_attributes?.years?.mappings),
      compatibility_mode: rowToEdit?.compatibility_mode || false,
    });
  }, [reset, rowToEdit]);

  const onSaved = useCallback(() => {
    handleSubmitExternal();
    successToast({ title: "Saved PowerSchool configuration" });
  }, [handleSubmitExternal, successToast]);

  const onSaveFailed = useCallback(
    (msg: string) => {
      errorToast({ title: "Failed to save PowerSchool configuration", description: msg });
    },
    [errorToast]
  );

  const updateProvider = useCallback(
    (id: string, row: ProviderData) => {
      // SM-API: PATCH /api/syncs/{id}
      void saveSisSync(row, id)
        .then(onSaved)
        .catch((e) => onSaveFailed(extractSyncError(e, "Unknown error")));
    },
    [onSaved, onSaveFailed]
  );

  const addProvider = useCallback(
    (data: ProviderData) => {
      // SM-API: POST /api/syncs
      void saveSisSync(data)
        .then(onSaved)
        .catch((e) => onSaveFailed(extractSyncError(e, "Unknown error")));
    },
    [onSaved, onSaveFailed]
  );

  const onSubmit = useCallback(
    (form: PowerSchoolInputs) => {
      const mergeConfig: MergeConfig = {
        priority: form.user_merge_priority,
        attachOnly: form.user_merge_attach_only,
      };
      if (form.user_merge_key === UserMergeKey.Computed) {
        mergeConfig.computed = {
          expression: form.user_merge_expression || "",
        };
      } else {
        mergeConfig.email = {
          stripped: form.user_merge_key === UserMergeKey.EmailStripped,
        };
      }
      // Build mappings and validate duplicates (single pass via YearAttributesToInput)
      const { mappings, duplicateKeys } = YearAttributesToInput(form.year_attribute_mappings);
      if (duplicateKeys.size > 0) {
        setError(`sync_year_attributes`, {
          type: "validate",
          message: "Cannot have duplicate values across inputs.",
        });
        return; // abort submit due to validation errors
      }

      const yearAttributes: AttributeMapping = {
        enabled: form.sync_year_attributes,
        mappings: mappings,
      };

      const apiData: ProviderData = {
        type: "powerschool",
        enabled: form.enabled,
        sync_classrooms: true,
        respect_enrolment_date: form.respect_enrolment_date,
        respect_sessions: form.respect_sessions,
        integration_type: "Powerschool",
        excluded_schools_list: form.excluded_schools_list,
        name: form.name,
        api_configuration: {
          endpoint_url: form.endpoint_url,
          token_url: form.token_url,
          client_id: form.client_id,
          client_secret: form.client_secret,
        },
        merge_config: mergeConfig,
        sync_attributes: {
          years: yearAttributes,
        },
        compatibility_mode: form.compatibility_mode,
      };

      if (form.id) {
        updateProvider(form.id, apiData);
      } else {
        addProvider(apiData);
      }
    },
    [addProvider, updateProvider, setError]
  );

  const handleHideClick = () => {
    handleHide();
  };

  return (
    <Modal isOpen={show} onClose={handleHideClick} isCentered scrollBehavior={"inside"}>
      <ModalOverlay />
      <ModalContent maxWidth={"800px"} maxHeight={"80%"}>
        <Box className="base-modal-close">
          <img
            alt="Close modal button"
            className="modal-close-icon"
            width="13"
            height="13"
            src="/static/images/icon_modal_close.svg"
            onClick={handleHideClick}
          />
        </Box>
        <ModalHeader>{!rowToEdit ? "Configure PowerSchool Sync" : "Edit PowerSchool Sync"}</ModalHeader>
        <ModalBody
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          {rowToEdit?.read_error && (
            <InlineNotification
              status="error"
              notificationTitle={"This configuration could not be read"}
              notificationDescription={`${rowToEdit.read_error}. The fields below show defaults, not the stored values — saving will replace the entire configuration.`}
            />
          )}
          <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/15099981623708"}>
            Configuring PowerSchool integration Guide
          </TextLink>

          <Text textAlign={"center"}>
            {
              "PowerSchool Integration requires a Plugin to be installed within PowerSchool. Please discuss with your Linewize Success or Account Manager to have this Plugin Provided."
            }
          </Text>

          <Text textAlign={"center"}>
            {"Provide your PowerSchool account details below to integrate your school system with Linewize Filter"}
          </Text>

          <Box as={"form"} sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px", fontSize: "14px" }}>
            <TextFieldControl
              styleType={"column"}
              fieldProps={{
                name: "name",
                label: "Name",
                tooltip: "The name of this SIS Sync Configuration.",
                control: control,
                errors: errors,
                rules: {
                  required: "A name is required.",
                },
              }}
            />

            <TextFieldControl
              styleType={"column"}
              fieldProps={{
                name: "endpoint_url",
                label: "Endpoint URL",
                tooltip: "The base PowerSchool endpoint URL.",
                control: control,
                errors: errors,
                rules: {
                  required: "An endpoint URL is required.",
                },
              }}
            />

            <TextFieldControl
              styleType={"column"}
              fieldProps={{
                name: "token_url",
                label: "OAuth2 Token URL",
                tooltip: "The endpoint URL for fetching the OAuth2 token.",
                control: control,
                errors: errors,
                rules: {
                  required: "A token URL is required.",
                },
              }}
            />

            <TextFieldControl
              styleType={"column"}
              fieldProps={{
                name: "client_id",
                label: "Client ID",
                tooltip: "OAuth2 Client ID for your PowerSchool app.",
                control: control,
                errors: errors,
                rules: {
                  required: "A client ID is required.",
                },
              }}
            />

            <TextFieldControl
              styleType={"column"}
              fieldProps={{
                name: "client_secret",
                label: "Client Secret",
                tooltip: "OAuth2 Client Secret for your PowerSchool app.",
                control: control,
                errors: errors,
                rules: {
                  required: "A client secret is required.",
                },
              }}
            />

            <CheckBoxControl
              fieldProps={{
                name: "respect_enrolment_date",
                label: "Respect Enrolment Date",
                tooltip:
                  "When this is ticked, syncs will not add students to classes if a student does not have a valid enrolment date for that class..",
                control: control,
                errors: errors,
              }}
            />

            <CheckBoxControl
              fieldProps={{
                name: "respect_sessions",
                label: "Respect Academic Sessions",
                tooltip: "When this is ticked, syncs exclude classes lacking an active academic session.",
                control: control,
                errors: errors,
              }}
            />

            <AttributeConfig clearErrors={clearErrors} syncYearAttributes={syncYearAttributesWatch} errors={errors} control={control} />

            <Accordion
              allowToggle
              sx={{
                border: `1px solid ${colors.neutrals["20"]}`,
                borderRadius: "6px",
              }}
            >
              <AccordionItem>
                <AccordionButton type="button" data-testid={"accordionBtn"}>
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      minHeight: "40px",
                      backgroundColor: colors.neutrals["20"],
                      alignContent: "center",
                      alignItems: "center",
                      padding: "12px",
                      gap: "8px",
                    }}
                  >
                    <AccordionIcon />

                    <Text fontWeight={500}>Advanced Configuration</Text>
                  </Box>
                </AccordionButton>
                <AccordionPanel
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "16px",
                    fontSize: "14px",
                    gap: "16px",
                  }}
                >
                  <Text>
                    Advanced sync settings are for complex deployments only. Consult with the Linewize support team before making any
                    changes.
                  </Text>
                  <Divider borderColor="neutrals.40" />
                  <MergeOptions register={register} errors={errors} control={control} mergeKey={watch("user_merge_key")} />
                  <Divider borderColor="neutrals.40" />
                  <InlineNotification
                    status="warning"
                    notificationTitle={"For existing deployments, compatibility mode may cause unexpected changes in sync behaviour."}
                    notificationDescription={
                      <>
                        Compatibility Mode only consumes OneRoster v1.1 endpoints that IMS Global states <strong>must</strong> be supported
                        for rostering conformance.
                      </>
                    }
                  />
                  <CheckBoxControl
                    fieldProps={{
                      name: "compatibility_mode",
                      label: "Compatibility mode",
                      control: control,
                      errors: errors,
                    }}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </ModalBody>

        <ModalFooter
          sx={{
            display: "flex",
            flexDirection: "column-reverse",
            justifyContent: "space-between",
            gap: "0.5rem",
          }}
        >
          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(onSubmit)}
            data-testid={"submitBtn"}
            mt={4}
            disabled={!isDirty || isSubmitting}
            colorScheme="teal"
            variant={"primary"}
          >
            Submit
          </Button>
          {multipleSyncsConfigured && (
            <InlineNotification
              status="warning"
              notificationTitle={"Important"}
              notificationDescription={
                "Multiple SIS Integrations are configured. If multiple syncs are required, ensure they do not duplicate data such as Students, Guardians, Schools or Classrooms."
              }
            />
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
